import { Ionicons } from "@expo/vector-icons";
import { type IThemePalette } from "../types";

/**
 * Standard Platform Symbol types across the Memories platform.
 */
export type PlatformSymbolType =
  | "owner"
  | "fivestar"
  | "saved"
  | "shared"
  | "heirloom"
  | "readonly"
  | "synced"
  | "you";

/**
 * Visual configuration for a platform symbol.
 */
export interface IPlatformSymbolConfig {
  /** Ionicons glyph name */
  icon: keyof typeof Ionicons.glyphMap;
  /** Primary accent color */
  color: string;
  /** Background tint color */
  backgroundColor: string;
  /** Border outline color */
  borderColor: string;
  /** Standard tooltip text */
  tooltip: string;
  /** Standard text label when showLabel is true */
  defaultLabel: string;
}

export type ISymbolTheme = Record<PlatformSymbolType, IPlatformSymbolConfig>;

/**
 * Pure factory generating the platform symbol component theme.
 * Standardizes all 8 canonical symbol badges with light/dark contrast tuning.
 *
 * @param palette - The active semantic theme palette.
 * @param isDark - Whether dark mode is currently active.
 * @returns Fully resolved ISymbolTheme.
 */
export function getSymbolTheme(palette: IThemePalette, isDark: boolean): ISymbolTheme {
  return {
    you: {
      icon: "person",
      color: isDark ? "#F5DE88" : "#B8860B",
      backgroundColor: isDark ? "rgba(245, 222, 136, 0.16)" : "rgba(212, 175, 55, 0.15)",
      borderColor: isDark ? "rgba(245, 222, 136, 0.4)" : "rgba(212, 175, 55, 0.38)",
      tooltip: "You (Account User)",
      defaultLabel: "You",
    },
    owner: {
      icon: "key",
      color: isDark ? "#F5DE88" : "#B8860B",
      backgroundColor: isDark ? "rgba(245, 222, 136, 0.16)" : "rgba(212, 175, 55, 0.15)",
      borderColor: isDark ? "rgba(245, 222, 136, 0.4)" : "rgba(212, 175, 55, 0.38)",
      tooltip: "Owner",
      defaultLabel: "Owner",
    },
    fivestar: {
      icon: "star",
      color: isDark ? "#FACC15" : "#CA8A04",
      backgroundColor: isDark ? "rgba(250, 204, 21, 0.18)" : "rgba(234, 179, 8, 0.15)",
      borderColor: isDark ? "rgba(250, 204, 21, 0.4)" : "rgba(234, 179, 8, 0.38)",
      tooltip: "5 stars",
      defaultLabel: "5 Stars",
    },
    saved: {
      icon: "cloud-done",
      color: isDark ? "#34D399" : "#10B981",
      backgroundColor: isDark ? "rgba(52, 211, 153, 0.18)" : "rgba(16, 185, 129, 0.14)",
      borderColor: isDark ? "rgba(52, 211, 153, 0.4)" : "rgba(16, 185, 129, 0.35)",
      tooltip: "Saved",
      defaultLabel: "Saved",
    },
    shared: {
      icon: "people",
      color: isDark ? "#38BDF8" : "#0284C7",
      backgroundColor: isDark ? "rgba(56, 189, 248, 0.16)" : "rgba(2, 132, 199, 0.12)",
      borderColor: isDark ? "rgba(56, 189, 248, 0.36)" : "rgba(2, 132, 199, 0.32)",
      tooltip: "Shared with you",
      defaultLabel: "Shared",
    },
    heirloom: {
      icon: "archive",
      color: isDark ? "#F59E0B" : "#B45309",
      backgroundColor: isDark ? "rgba(245, 158, 11, 0.16)" : "rgba(180, 83, 9, 0.12)",
      borderColor: isDark ? "rgba(245, 158, 11, 0.38)" : "rgba(180, 83, 9, 0.35)",
      tooltip: "Physical Heirloom",
      defaultLabel: "Heirloom",
    },
    readonly: {
      icon: "lock-closed",
      color: isDark ? "#94A3B8" : "#64748B",
      backgroundColor: isDark ? "rgba(148, 163, 184, 0.16)" : "rgba(100, 116, 139, 0.12)",
      borderColor: isDark ? "rgba(148, 163, 184, 0.35)" : "rgba(100, 116, 139, 0.32)",
      tooltip: "Read-only",
      defaultLabel: "Read-Only",
    },
    synced: {
      icon: "cloud-download",
      color: isDark ? "#38BDF8" : "#0EA5E9",
      backgroundColor: isDark ? "rgba(56, 189, 248, 0.16)" : "rgba(14, 165, 233, 0.12)",
      borderColor: isDark ? "rgba(56, 189, 248, 0.36)" : "rgba(14, 165, 233, 0.32)",
      tooltip: "Available Offline",
      defaultLabel: "Synced",
    },
  };
}

/**
 * Static registry of Platform Symbols for backwards compatibility.
 */
export const PLATFORM_SYMBOLS: Record<PlatformSymbolType, IPlatformSymbolConfig> = {
  you: {
    icon: "person",
    color: "#D4AF37",
    backgroundColor: "rgba(212, 175, 55, 0.15)",
    borderColor: "rgba(212, 175, 55, 0.38)",
    tooltip: "You (Account User)",
    defaultLabel: "You",
  },
  owner: {
    icon: "key",
    color: "#D4AF37",
    backgroundColor: "rgba(212, 175, 55, 0.15)",
    borderColor: "rgba(212, 175, 55, 0.38)",
    tooltip: "Owner",
    defaultLabel: "Owner",
  },
  fivestar: {
    icon: "star",
    color: "#EAB308",
    backgroundColor: "rgba(234, 179, 8, 0.15)",
    borderColor: "rgba(234, 179, 8, 0.38)",
    tooltip: "5 stars",
    defaultLabel: "5 Stars",
  },
  saved: {
    icon: "cloud-done",
    color: "#10B981",
    backgroundColor: "rgba(16, 185, 129, 0.14)",
    borderColor: "rgba(16, 185, 129, 0.35)",
    tooltip: "Saved",
    defaultLabel: "Saved",
  },
  shared: {
    icon: "people",
    color: "#0284C7",
    backgroundColor: "rgba(2, 132, 199, 0.12)",
    borderColor: "rgba(2, 132, 199, 0.32)",
    tooltip: "Shared with you",
    defaultLabel: "Shared",
  },
  heirloom: {
    icon: "archive",
    color: "#B45309",
    backgroundColor: "rgba(180, 83, 9, 0.12)",
    borderColor: "rgba(180, 83, 9, 0.35)",
    tooltip: "Physical Heirloom",
    defaultLabel: "Heirloom",
  },
  readonly: {
    icon: "lock-closed",
    color: "#64748B",
    backgroundColor: "rgba(100, 116, 139, 0.12)",
    borderColor: "rgba(100, 116, 139, 0.32)",
    tooltip: "Read-only",
    defaultLabel: "Read-Only",
  },
  synced: {
    icon: "cloud-download",
    color: "#0EA5E9",
    backgroundColor: "rgba(14, 165, 233, 0.12)",
    borderColor: "rgba(14, 165, 233, 0.32)",
    tooltip: "Available Offline",
    defaultLabel: "Synced",
  },
};
